import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, formatQty } from "@/components/Shared";
import { toast } from "sonner";
import { Plus, Trash2, ChefHat, Copy, Printer, X } from "lucide-react";
import SearchBar, { applyTextFilter } from "@/components/SearchBar";
import { packageLabel, productUnitOptions, unitRatio } from "@/lib/units";
import IngredientCombobox from "@/components/IngredientCombobox";
import { printRecipe } from "@/lib/printRecipe";

export default function Recipes() {
  const [products, setProducts] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [outputQty, setOutputQty] = useState(1);
  const [outputUnit, setOutputUnit] = useState("piece");
  // Each ingredient: { kind: "material" | "product", id, quantity }
  const [ingredients, setIngredients] = useState([{ kind: "material", id: "", quantity: "" }]);
  const [isDuplicating, setIsDuplicating] = useState(false);

  // Print dialog
  const [printDialog, setPrintDialog] = useState(null); // { recipe }
  const [printQty, setPrintQty] = useState(10);

  const load = async () => {
    const [p, m, r] = await Promise.all([
      api.get("/products"),
      api.get("/materials"),
      api.get("/recipes"),
    ]);
    setProducts(p.data);
    setMaterials(m.data);
    setRecipes(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setSelectedProduct("");
    setOutputQty(1);
    setOutputUnit("piece");
    setIngredients([{ kind: "material", id: "", quantity: "" }]);
    setIsDuplicating(false);
  };

  const loadIntoEditor = (pid, recipeOverride) => {
    setSelectedProduct(pid);
    // V režimu duplikace pouze nastavíme cílový výrobek a NEPŘEPISUJEME suroviny.
    if (isDuplicating) {
      if (!pid) return;
      const existing = recipes.find((r) => r.product_id === pid);
      if (existing) {
        const ok = window.confirm(
          `Výrobek "${products.find((p) => p.id === pid)?.name}" už má technologický postup. ` +
            `Pokud uložíte, stávající postup bude přepsán duplikovanými surovinami. Pokračovat?`
        );
        if (!ok) {
          setSelectedProduct("");
        }
      }
      return;
    }
    const existing = recipeOverride || recipes.find((r) => r.product_id === pid);
    if (existing) {
      setOutputQty(existing.output_quantity);
      setOutputUnit(existing.output_unit || "piece");
      setIngredients(
        existing.ingredients.map((i) => ({
          kind: i.product_id ? "product" : "material",
          id: i.product_id || i.material_id || "",
          quantity: i.quantity,
        }))
      );
    } else {
      setOutputQty(1);
      const prod = products.find((p) => p.id === pid);
      setOutputUnit(prod?.default_production_unit || "piece");
      setIngredients([{ kind: "material", id: "", quantity: "" }]);
    }
  };

  const duplicate = (r) => {
    // Start a new recipe in editor: keep ingredients but clear product (user picks new)
    setIsDuplicating(true);
    setSelectedProduct("");
    setOutputQty(r.output_quantity);
    setOutputUnit(r.output_unit || "piece");
    setIngredients(
      r.ingredients.map((i) => ({
        kind: i.product_id ? "product" : "material",
        id: i.product_id || i.material_id || "",
        quantity: i.quantity,
      }))
    );
    toast.info(
      "Postup zduplikován — vyberte cílový výrobek a uložte. Suroviny se zachovají."
    );
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!selectedProduct) {
      toast.error("Vyberte výrobek");
      return;
    }
    const ings = ingredients
      .filter((i) => i.id && i.quantity)
      .map((i) =>
        i.kind === "product"
          ? { product_id: i.id, quantity: parseFloat(i.quantity) }
          : { material_id: i.id, quantity: parseFloat(i.quantity) }
      );
    if (ings.length === 0) {
      toast.error("Přidejte alespoň jednu ingredienci");
      return;
    }
    // Sanity — výrobek nemůže být polotovarem sám sebe
    if (ings.some((i) => i.product_id === selectedProduct)) {
      toast.error("Výrobek nemůže být polotovarem sám sebe");
      return;
    }
    try {
      await api.post("/recipes", {
        product_id: selectedProduct,
        output_quantity: parseFloat(outputQty) || 1,
        output_unit: outputUnit,
        ingredients: ings,
      });
      toast.success("Postup uložen");
      reset();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    }
  };

  const removeRecipe = async (rid) => {
    if (!window.confirm("Smazat postup?")) return;
    await api.delete(`/recipes/${rid}`);
    await load();
    reset();
  };

  const matName = (id) => materials.find((m) => m.id === id)?.name || "—";
  const matUnit = (id) => materials.find((m) => m.id === id)?.unit || "";
  const prodName = (id) => products.find((p) => p.id === id)?.name || "—";

  const [search, setSearch] = useState("");
  const filteredRecipes = useMemo(
    () => applyTextFilter(recipes, search, (r) => [prodName(r.product_id)]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recipes, search, products]
  );

  // Pro IngredientCombobox: nepovolit aktuálně editovaný výrobek jako polotovar (anti-cycle UX)
  const productOptionsForIngredients = useMemo(
    () => products.filter((p) => !p.deleted && p.id !== selectedProduct),
    [products, selectedProduct]
  );

  const openPrint = (recipe) => {
    const product = products.find((p) => p.id === recipe.product_id);
    // default = 1 dávka v ks
    const outPieces = Number(recipe.output_quantity) * unitRatio(product, recipe.output_unit);
    setPrintQty(Math.max(1, Math.round(outPieces)));
    setPrintDialog({ recipe, product });
  };

  const doPrint = () => {
    if (!printDialog) return;
    try {
      printRecipe({
        product: printDialog.product,
        recipe: printDialog.recipe,
        products,
        recipes,
        materials,
        qtyPieces: Number(printQty) || 1,
      });
      setPrintDialog(null);
    } catch (e) {
      toast.error(e.message || "Chyba při tisku");
    }
  };

  return (
    <div data-testid="recipes-page">
      <PageHeader
        eyebrow="Výroba"
        title="Technologické postupy"
        description="Pro každý výrobek definujte suroviny i případné polotovary (jiné výrobky). Při výrobě se polotovary kaskádově rozkládají až na suroviny a odečítají se FIFO."
      />

      <div className="space-y-6">
        <div className="card-cafe p-6 space-y-5">
          <div className="flex items-center gap-2 flex-wrap">
            <ChefHat className="w-5 h-5 text-[#3A2D27]" />
            <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>
              Editor postupu
            </h3>
            {isDuplicating && (
              <span
                data-testid="duplicating-badge"
                className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border"
                style={{ background: "#D4A37326", color: "#8a6622", borderColor: "#D4A37366" }}
              >
                <Copy className="w-3 h-3" /> Duplikuji — vyberte cílový výrobek
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="text-sm font-medium mb-1.5">Výrobek</div>
              <select
                data-testid="recipe-product-select"
                className="input-cafe w-full"
                value={selectedProduct}
                onChange={(e) => loadIntoEditor(e.target.value)}
              >
                <option value="">— vyberte výrobek —</option>
                {products.filter((p) => !p.deleted).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-sm font-medium mb-1.5">Výtěžek 1 dávky</div>
              <input
                data-testid="recipe-output-qty"
                type="number"
                inputMode="decimal"
                step="0.001"
                min="0.001"
                className="input-cafe w-full"
                value={outputQty}
                onChange={(e) => setOutputQty(e.target.value)}
              />
            </div>
            <div>
              <div className="text-sm font-medium mb-1.5">Jednotka výtěžku</div>
              {(() => {
                const prod = products.find((p) => p.id === selectedProduct);
                const opts = productUnitOptions(prod);
                return (
                  <div className="inline-flex flex-wrap rounded-md border border-[#E5E1D8] overflow-hidden">
                    {opts.map((o) => (
                      <button
                        key={o.value}
                        data-testid={`recipe-output-unit-${o.value}`}
                        type="button"
                        onClick={() => setOutputUnit(o.value)}
                        className={`px-4 py-2 text-sm ${
                          outputUnit === o.value ? "bg-[#3A2D27] text-white" : "bg-white text-[#3A2D27]"
                        }`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                );
              })()}
              {selectedProduct && outputUnit !== "piece" && (
                <div className="text-xs text-[#57534E] mt-1">
                  1 dávka = {(parseFloat(outputQty) || 0) * unitRatio(products.find((p) => p.id === selectedProduct), outputUnit)} ks
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="label-eyebrow">Suroviny a polotovary na dávku</div>
            {ingredients.map((ing, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-7" data-testid={`recipe-ing-pick-${idx}`}>
                  <IngredientCombobox
                    materials={materials}
                    products={productOptionsForIngredients}
                    value={ing.id ? { kind: ing.kind, id: ing.id } : null}
                    onChange={(v) => {
                      const next = [...ingredients];
                      next[idx].kind = v?.kind || "material";
                      next[idx].id = v?.id || "";
                      setIngredients(next);
                    }}
                    testid={`recipe-ing-combobox-${idx}`}
                  />
                </div>
                <input
                  data-testid={`recipe-ing-qty-${idx}`}
                  type="number"
                  inputMode="decimal"
                  step="0.001"
                  min="0"
                  placeholder={ing.kind === "product" ? "ks" : "množství"}
                  className="input-cafe col-span-4"
                  value={ing.quantity}
                  onChange={(e) => {
                    const next = [...ingredients];
                    next[idx].quantity = e.target.value;
                    setIngredients(next);
                  }}
                />
                <button
                  data-testid={`recipe-ing-remove-${idx}`}
                  type="button"
                  onClick={() => setIngredients(ingredients.filter((_, i) => i !== idx))}
                  className="col-span-1 h-10 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0] grid place-items-center"
                >
                  <Trash2 className="w-4 h-4 text-[#57534E]" />
                </button>
              </div>
            ))}
            <button
              data-testid="recipe-add-ing"
              type="button"
              onClick={() => setIngredients([...ingredients, { kind: "material", id: "", quantity: "" }])}
              className="text-sm inline-flex items-center gap-1.5 text-[#3A2D27] hover:text-[#D4A373]"
            >
              <Plus className="w-4 h-4" /> Přidat surovinu / polotovar
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            <button data-testid="recipe-save" onClick={save} className="btn-primary">
              Uložit postup
            </button>
            {(selectedProduct || isDuplicating) && (
              <button
                data-testid="recipe-cancel-edit"
                onClick={reset}
                className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0] text-sm"
              >
                Zrušit
              </button>
            )}
          </div>
        </div>

        <div className="card-cafe p-6">
          <div className="label-eyebrow mb-4">Uložené postupy ({filteredRecipes.length})</div>
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Hledat podle názvu výrobku…"
            testid="recipes-search"
          />
          {filteredRecipes.length === 0 ? (
            <div className="text-sm text-[#57534E]">Bez výsledků.</div>
          ) : (
            <ul className="space-y-3">
              {filteredRecipes.map((r) => (
                <li
                  key={r.id}
                  className="border border-[#E5E1D8] rounded-lg p-3 hover:border-[#D4A373] transition-colors"
                >
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="font-medium">{prodName(r.product_id)}</div>
                      <div className="text-xs text-[#57534E]">
                        Dávka = {formatQty(r.output_quantity)}{" "}
                        {r.output_unit && r.output_unit !== "piece"
                          ? (r.output_unit === "package"
                              ? packageLabel(products.find((p) => p.id === r.product_id))
                              : r.output_unit)
                          : "ks"}
                        {" · "}{r.ingredients.length} položek
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <button
                        data-testid={`recipe-print-${r.id}`}
                        onClick={() => openPrint(r)}
                        className="text-xs px-2 py-1 rounded border border-[#E5E1D8] hover:bg-[#F9F6F0] inline-flex items-center gap-1"
                      >
                        <Printer className="w-3.5 h-3.5" /> Tisk
                      </button>
                      <button
                        data-testid={`recipe-duplicate-${r.id}`}
                        onClick={() => duplicate(r)}
                        className="text-xs px-2 py-1 rounded border border-[#E5E1D8] hover:bg-[#F9F6F0] inline-flex items-center gap-1"
                      >
                        <Copy className="w-3.5 h-3.5" /> Duplikovat
                      </button>
                      <button
                        data-testid={`recipe-edit-${r.id}`}
                        onClick={() => loadIntoEditor(r.product_id)}
                        className="text-xs px-2 py-1 rounded border border-[#E5E1D8] hover:bg-[#F9F6F0]"
                      >
                        Upravit
                      </button>
                      <button
                        data-testid={`recipe-del-${r.id}`}
                        onClick={() => removeRecipe(r.id)}
                        className="text-xs px-2 py-1 rounded border border-[#E5E1D8] hover:bg-[#F9F6F0] text-[#9E4747]"
                      >
                        Smazat
                      </button>
                    </div>
                  </div>
                  <ul className="mt-2 text-xs text-[#57534E] space-y-0.5">
                    {r.ingredients.map((i, k) => {
                      if (i.product_id) {
                        return (
                          <li key={k}>
                            ·{" "}
                            <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-[#D4A37326] text-[#8a6622] border border-[#D4A37366] mr-1">
                              polotovar
                            </span>
                            {prodName(i.product_id)} —{" "}
                            <span className="mono">{formatQty(i.quantity, "ks")}</span>
                          </li>
                        );
                      }
                      return (
                        <li key={k}>
                          · {matName(i.material_id)} —{" "}
                          <span className="mono">{formatQty(i.quantity, matUnit(i.material_id))}</span>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Print qty dialog */}
      {printDialog && (
        <div
          data-testid="print-qty-dialog"
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          onClick={() => setPrintDialog(null)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="label-eyebrow mb-1">Tisk technologického postupu</div>
                <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>
                  {printDialog.product?.name}
                </h3>
              </div>
              <button
                onClick={() => setPrintDialog(null)}
                className="p-2 -mr-2 -mt-2 rounded hover:bg-[#F9F6F0]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <label className="block">
              <div className="text-sm font-medium mb-1.5">Kolik kusů chcete vyrobit?</div>
              <input
                data-testid="print-qty-input"
                autoFocus
                type="number"
                inputMode="numeric"
                min="1"
                step="1"
                className="input-cafe w-full text-2xl"
                value={printQty}
                onChange={(e) => setPrintQty(e.target.value)}
              />
              <div className="text-xs text-[#57534E] mt-1.5">
                Postup se přepočítá podle zadaného množství. Polotovary se rozloží na suroviny.
              </div>
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setPrintDialog(null)}
                className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]"
              >
                Zrušit
              </button>
              <button
                data-testid="print-qty-confirm"
                onClick={doPrint}
                className="btn-primary inline-flex items-center gap-1.5"
              >
                <Printer className="w-4 h-4" /> Tisknout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
